import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getClientDetails, updateClientDetails, getFaxTemplates, getSensitiveFields } from "./ClientView.actions";
import { CLIENT_UPDATE_SENSITIVE_FIELDS } from "../../../lookup/ClientViewConstants";
import ConfirmationModal from "components/ConfirmationModal";
import Spinner from "components/Spinner";
import { formatPhoneNumber } from "services/UtilityService";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import './ClientView.css';
import withRouter from "utils/withRouter";

const ClientView = (props) => {
  const { clientInfoUuid } = props.match.params;
  const history = props.history;
  const dispatch = useDispatch();

  const {
    details: client,
    loadingDetails,
    updating,
    sensitiveFields,
  } = useSelector(
    (state) => state.clientView
  );

  const {
    faxTemplates,
    loadingFaxTemplates,
  } = useSelector((state) => state.customerView);

  const [formState, setFormState] = useState(null);
  const [originalFormState, setOriginalFormState] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const canManage = hasRoleIn([PERMISSIONS.PERM_CONFIG_TOOL_MANAGE]);
  const isReadOnly = !canManage;

  useEffect(() => {
    dispatch(getClientDetails(clientInfoUuid));
    dispatch(getFaxTemplates());
    dispatch(getSensitiveFields(CLIENT_UPDATE_SENSITIVE_FIELDS));
  }, [dispatch, clientInfoUuid]);

  useEffect(() => {
    if (client) {
      const newFormState = {
        ...client,
        supportNumber: client.supportNumber ? client.supportNumber.replace(/[^0-9]/g, '').slice(0, 10) : '',
        faxNumberInbound: client.faxNumberInbound ? client.faxNumberInbound.replace(/[^0-9]/g, '').slice(0, 10) : '',
        faxNumberOutbound: client.faxNumberOutbound ? client.faxNumberOutbound.replace(/[^0-9]/g, '').slice(0, 10) : '',
      };
      // Set default fax template if not set and templates are available
      if (!newFormState.faxTemplate && faxTemplates) {
        const defaultTemplateKey = Object.keys(faxTemplates).find(
          key => faxTemplates[key]?.default === true
        );
        if (defaultTemplateKey) {
          newFormState.faxTemplate = defaultTemplateKey;
        }
      }
      setFormState(newFormState);
      setOriginalFormState({ ...client });
    }
  }, [client, faxTemplates]);

  useEffect(() => {
    if (faxTemplates && formState && !formState.faxTemplate) {
      // Find the default template
      const defaultTemplateKey = Object.keys(faxTemplates).find(
        key => faxTemplates[key]?.default === true
      );
      if (defaultTemplateKey) {
        setFormState(prevState => ({
          ...prevState,
          faxTemplate: defaultTemplateKey
        }));
      }
    }
  }, [faxTemplates, formState]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let newValue = value;

    if (['supportNumber', 'faxNumberInbound', 'faxNumberOutbound'].includes(name)) {
      // Only allow digits
      newValue = value.replace(/[^0-9]/g, '').slice(0, 10);
    }

    setFormState(prevState => ({
      ...prevState,
      [name]: newValue
    }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: checked
    }));
  };

  const handleModalConfirmSave = () => {
    setShowConfirmationModal(false);
    proceedWithSave();
  };

  const proceedWithSave = () => {
    dispatch(updateClientDetails(clientInfoUuid, formState)).then(
      (response) => {
        if (response && response.type === "UPDATE_CLIENT_DETAILS_SUCCESS") {
          history.goBack();
        }
      }
    );
  };

  const handleCancel = () => {
    history.goBack();
  };

  const isValid = () => {
    return formState.programName && formState.faxTemplate;
  };

  const handleSave = async () => {
    if (!isValid()) return;

    const changedFields = sensitiveFields.filter(field => originalFormState[field] !== formState[field]);
    if (changedFields.length > 0) {
      setShowConfirmationModal(true);
    } else {
      proceedWithSave();
    }
  };

  const isFieldSensitive = (fieldName) => {
    return sensitiveFields.includes(fieldName);
  };

  const renderLabel = (label, fieldName) => {
    return (
      <Form.Label>
        {label}
        {isFieldSensitive(fieldName) && (
          <span className="badge badge-warning ml-2" style={{ fontSize: '0.7em', verticalAlign: 'middle' }}>Sensitive</span>
        )}
      </Form.Label>
    );
  };

  const renderSwitch = (label, fieldName, checked, onChange, id) => {
    return (
      <Form.Group>
         <div className="d-flex align-items-center">
            <Form.Check
              type="switch"
              id={id}
              label={label}
              name={fieldName}
              checked={checked}
              onChange={onChange}
              disabled={isReadOnly}
            />
            {isFieldSensitive(fieldName) && (
              <span className="badge badge-warning ml-2" style={{ fontSize: '0.7em' }}>Sensitive</span>
            )}
        </div>
      </Form.Group>
    );
  };

  if (loadingDetails || loadingFaxTemplates || !client || !formState) {
    return <Spinner />;
  }

  return (
    <Container fluid>
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleModalConfirmSave}
        title="Confirm Save"
        body="You have modified sensitive fields. Are you sure you want to save these changes?"
      />
      <Row>
        <Col md={12}>
          <h5>{client.clientName}</h5>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mt-4">
          <div className="client-details-wrapper">
            {updating && (
              <div className="updating-overlay">
                <Spinner />
              </div>
            )}
            <Card>
              <Card.Header>
                <Card.Title as="h6">Identity</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Customer Name</Form.Label>
                      <Form.Control type="text" value={formState.customerName} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Client Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" name="clientName" value={formState.clientName} onChange={handleInputChange} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      <Form.Label>Client Abbreviation <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" name="clientNameAbbreviation" value={formState.clientNameAbbreviation} onChange={handleInputChange} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      {renderLabel(<>Program Name <span className="text-danger">*</span></>, "programName")}
                      <Form.Control type="text" name="programName" value={formState.programName} onChange={handleInputChange} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      {renderLabel("Logo URL", "logoUrl")}
                      <Form.Control type="text" name="logoUrl" value={formState.logoUrl} onChange={handleInputChange} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
        <Col md={12} className="mt-4">
          <div className="client-details-wrapper">
            <Card>
              <Card.Header>
                <Card.Title as="h6">Contact</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                  <Col md={6}>
                    <Form.Group>
                      {renderLabel("Support Number", "supportNumber")}
                      <Form.Control type="text" name="supportNumber" value={formatPhoneNumber(formState.supportNumber)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      {renderLabel("Inbound Fax Number", "faxNumberInbound")}
                      <Form.Control type="text" name="faxNumberInbound" value={formatPhoneNumber(formState.faxNumberInbound)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      {renderLabel("Outbound Fax Number", "faxNumberOutbound")}
                      <Form.Control type="text" name="faxNumberOutbound" value={formatPhoneNumber(formState.faxNumberOutbound)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md={12}>
          <div className="client-details-wrapper">
            <Card>
              <Card.Header>
                <Card.Title as="h6">Flags</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={4}>
                        {renderSwitch("Email Notification", "isEmailNotificationEnabled", formState.isEmailNotificationEnabled, handleCheckboxChange, "email-notification-switch")}
                    </Col>
                    <Col md={4} className="mt-2">
                        {renderSwitch("Member Engagement", "isMemberEngagementEnabled", formState.isMemberEngagementEnabled, handleCheckboxChange, "member-engagement-switch")}
                    </Col>
                    <Col md={4} className="mt-2">
                        {renderSwitch("Prescriber Email", "isPrescriberEmailEnabled", formState.isPrescriberEmailEnabled, handleCheckboxChange, "prescriber-email-switch")}
                    </Col>
                    <Col md={4} className="mt-2">
                        {renderSwitch("Has Custom Switches", "hasCustomSwitches", formState.hasCustomSwitches, handleCheckboxChange, "custom-switches-switch")}
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Group>
                        {renderLabel("Language", "language")}
                        <Form.Control as="select" name="language" value={formState.language} onChange={handleInputChange} disabled={isReadOnly}>
                          <option value="ENGLISH">English</option>
                          <option value="SPANISH">Spanish</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        {renderLabel(<>Fax Template <span className="text-danger">*</span></>, "faxTemplate")}
                        <Form.Control as="select" name="faxTemplate" value={formState.faxTemplate} onChange={handleInputChange} disabled={isReadOnly}>
                          <option value="">Select a template</option>
                          {faxTemplates && typeof faxTemplates === 'object'
                            ? Object.entries(faxTemplates).map(([key, template]) => {
                                const displayName = typeof template === 'object' && template !== null && template.displayName
                                  ? template.displayName
                                  : typeof template === 'string'
                                  ? template
                                  : key;
                                const isDisabled = typeof template === 'object' && template !== null && template.disabled === true;
                                const displayText = isDisabled ? `${displayName} - Deprecated` : displayName;
                                return (
                                  <option key={key} value={key} disabled={isDisabled}>
                                    {displayText}
                                  </option>
                                );
                              })
                            : null}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={6} className='mt-2'>
                      <Form.Group>
                        {renderLabel("Population Type", "populationType")}
                        <Form.Control as="select" name="populationType" value={formState.populationType} onChange={handleInputChange} disabled={isReadOnly}>
                          <option value="medicare">Medicare</option>
                          <option value="commercial">Commercial</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
      {canManage && (
        <Row className="mt-4">
          <Col md={12} className="text-right">
            <Button variant="secondary" onClick={handleCancel} className="mr-2">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={updating || !isValid()} className="save-button">
              Save
            </Button>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default withRouter(ClientView);
